import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { Department, departmentService } from '../services/departmentService';
import { useAuthContext } from './AuthContext';

interface DepartmentContextType {
  departments: Department[];
  isLoading: boolean;
  error: string | null;
  fetchDepartments: () => Promise<void>;
  addDepartment: (data: { name: string; description?: string; manager_id?: number; member_ids?: number[] }) => Promise<Department>;
  updateDepartment: (id: number, data: { name?: string; description?: string; manager_id?: number; member_ids?: number[] }) => Promise<Department>;
  deleteDepartment: (id: number) => Promise<void>;
}

const DepartmentContext = createContext<DepartmentContextType | undefined>(undefined);

export const DepartmentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthContext();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDepartments = useCallback(async () => {
    if (!isAuthenticated) {
      setDepartments([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await departmentService.getAllDepartments();
      setDepartments(data || []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch departments';
      console.error('[DepartmentContext] Error fetching departments:', message);
      setError(message);
      setDepartments([]);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch departments automatically when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchDepartments();
    } else {
      setDepartments([]);
      setIsLoading(false);
    }
  }, [isAuthenticated, fetchDepartments]);

  const addDepartment = useCallback(async (data: { name: string; description?: string; manager_id?: number; member_ids?: number[] }) => {
    try {
      setError(null);
      const newDept = await departmentService.createDepartment(data);
      setDepartments(prev => [...prev, newDept]);
      return newDept;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add department';
      console.error('[DepartmentContext] Error adding department:', message);
      setError(message);
      throw err;
    }
  }, []);

  const updateDepartment = useCallback(async (id: number, data: { name?: string; description?: string; manager_id?: number; member_ids?: number[] }) => {
    try {
      setError(null);
      const updated = await departmentService.updateDepartment(id, data);
      setDepartments(prev =>
        prev.map(dept => dept.id === id ? updated : dept)
      );
      return updated;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update department';
      console.error('[DepartmentContext] Error updating department:', message);
      setError(message);
      throw err;
    }
  }, []);

  const deleteDepartment = useCallback(async (id: number) => {
    try {
      setError(null);
      console.log('[DepartmentContext] Deleting department:', id);
      await departmentService.deleteDepartment(id);
      console.log('[DepartmentContext] Department deleted');
      setDepartments(prev => prev.filter(dept => dept.id !== id));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete department';
      console.error('[DepartmentContext] Error deleting department:', message);
      setError(message);
      throw err;
    }
  }, []);

  const value: DepartmentContextType = {
    departments,
    isLoading,
    error,
    fetchDepartments,
    addDepartment,
    updateDepartment,
    deleteDepartment
  };

  return (
    <DepartmentContext.Provider value={value}>
      {children}
    </DepartmentContext.Provider>
  );
};

export const useDepartments = () => {
  const context = useContext(DepartmentContext);
  if (context === undefined) {
    throw new Error('useDepartments must be used within DepartmentProvider');
  }
  return context;
};
